import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router";

import { getFolderAncestry, selectFolderById, useGetAccountFolders } from "fond/api";
import { NavigateError } from "fond/navigation/NavigateError";
import { FoldersProjectList } from "fond/projects";
import { Navigation } from "fond/projects/Navigation";
import { updateFilter } from "fond/projects/redux";
import { Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { PageContent } from "fond/widgets";

import NewItemButton from "../MainWorkspace/NewItemButton";

interface RouteParams {
  folderId: string;
}

/**
 * A top level component used for displaying project.
 */
const Folders: React.FC = () => {
  const dispatch = useAppDispatch();
  const filters = useSelector((state: Store) => state.projects.filters);

  // If no filter is applied (e.g. because the user used the 'back' button to return from the account management
  // page) then we apply the 'workspace' filter. This might not strictly be accurate (e.g. if the user was
  // viewing this folder via 'Shared with me'), but it aligns with what happens if the user navigates
  // to the folder directly via the URL.
  React.useEffect(() => {
    if (filters === null) {
      dispatch(updateFilter("workspace"));
    }
  }, [filters, dispatch]);

  const { isFetching: isFoldersFetching } = useGetAccountFolders();
  const { folderId } = useParams<keyof RouteParams>();
  const ancestors = useSelector((state: Store) => getFolderAncestry(state, folderId));
  const reverseAncestorNames = React.useMemo(() => ancestors.toReversed().map((ancestor) => ancestor.Name), [ancestors]);

  const currentFolder = useSelector((state: Store) => (folderId ? selectFolderById(state, folderId) : undefined));

  /**
   * If folderId is not null but currentFolder is null, it indicates that the current user has no access to the folder,
   * or the folder does not exist.
   * redirect to projects landing page.
   */
  if (folderId && !currentFolder && !isFoldersFetching) {
    return <NavigateError to="/404" resourceType="FOLDER" />;
  }
  if (folderId && currentFolder && currentFolder.MultiProject) {
    return <Navigate to={`/cities/${folderId}`} replace />;
  }
  if (folderId && currentFolder && currentFolder.MultiReport) {
    return <Navigate to={`/city/report/${folderId}`} replace />;
  }

  return (
    <PageContent
      windowTitle={reverseAncestorNames.join(" /  ")}
      title={currentFolder?.Name ?? ""}
      action={filters === "workspace" && currentFolder && <NewItemButton source="new-item-button" />}
      mainContent={<FoldersProjectList />}
      navigation={<Navigation />}
      testId="folders-content"
    />
  );
};

export default Folders;
